import React, { useCallback, useState } from 'react';
import classes from '~/app/common/scss/list.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import { Part, SavePartResponse } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes, PartTypes } from '~/app/data/statuses/issue';
import PartRowMenu from '../PartRowMenu';
import ManualPartForm from '../Add/ManualPartForm';
import QuantityEditForm from '../QuantityEditForm';
import { formatPrice } from '~/app/helpers/format';
import { getPartStoreKey, isPartsEditable } from '../helper';

export interface PartsProps {
  type: PartLinkTypes
}

export type ListComponent = React.FC<PartsProps>;

const List: ListComponent = ({ type }) => {

  const basePath = getPartStoreKey(type);
  const parts = useStoreState(state => state[basePath].data.parts ?? []);
  const id = useStoreState(state => state[basePath].data.id);
  const status = useStoreState(state => state[basePath].data.status);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const setTempPartsAction = useStoreActions(actions => actions[basePath].setTempParts);
  const savePartAction = useStoreActions(actions => actions[basePath].savePart);
  const fetchPartsAction = useStoreActions(actions => actions[basePath].fetchParts);

  const [partForEdit, setPartForEdit] = useState<Part>();
  const [isOpenManualPartForm, setIsOpenManualPartForm] = useState(false);
  const [isOpenQuantityEditForm, setIsOpenQuantityEditForm] = useState(false);

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const addPartPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit = !netWorkStatus && isPartsEditable(type, status) && addPartPermission;

  const persistParts = useCallback((nextParts: Part[]) => {
    setTempPartsAction(nextParts);
    return savePartAction({
      id: String(id),
      parts: nextParts,
    }).then((res: SavePartResponse) => {
      if (res.success) {
        fetchPartsAction(String(id));
      }
      return res;
    });
  }, [id, setTempPartsAction, savePartAction, fetchPartsAction]);

  const onRemove = useCallback((partToRemove: Part) => {
    const nextParts = parts.map(part =>
      part.id === partToRemove.id
        ? { ...part, deleted: true, added: undefined }
        : { ...part, added: undefined }
    );
    persistParts(nextParts);
  }, [parts, persistParts]);

  const onEdit = useCallback((part: Part) => {
    setTempPartsAction(parts.map(m => ({ ...m, added: undefined })));
    setPartForEdit(part);
    if (part.type === PartTypes.MANUAL) {
      setIsOpenManualPartForm(true);
    } else {
      setIsOpenQuantityEditForm(true);
    }
  }, [parts, setTempPartsAction]);

  const closeManualForm = useCallback(() => {
    setIsOpenManualPartForm(false);
    setPartForEdit(undefined);
  }, []);

  const closeQuantityForm = useCallback(() => {
    setIsOpenQuantityEditForm(false);
    setPartForEdit(undefined);
  }, []);

  const onQuantitySave = useCallback((part: Part, quantity: number) => {
    const nextParts = parts.map(m =>
      m.id === part.id
        ? { ...m, quantity, added: undefined }
        : { ...m, added: undefined }
    );
    persistParts(nextParts);
  }, [parts, persistParts]);

  return (
    <div>
      {parts.map((part) => {
        const lineTotal = (Number(part.price_per_unit) || 0) * (Number(part.quantity) || 0);
        return (
          <div className={classes['partBox']} key={part.id}>
            <div className={classes['row']}>
              <span className={classes['partName']}>{part.article_name}</span>
              <span className={classes['linePrice']}>{formatPrice(lineTotal)}</span>
            </div>
            <div className={classes['row']}>
              <span className={classes['partNumber']}>
                {t('partNo')}: {part.article_no} · {part.quantity} {t('item')}
              </span>
              {canEdit && (
                <PartRowMenu
                  part={part}
                  onEdit={onEdit}
                  onRemove={onRemove}
                />
              )}
            </div>
          </div>
        );
      })}
      {canEdit && (
        <>
          <ManualPartForm
            open={isOpenManualPartForm}
            onClose={closeManualForm}
            part={partForEdit}
            type={type}
            persistOnSave
          />
          <QuantityEditForm
            open={isOpenQuantityEditForm}
            onClose={closeQuantityForm}
            part={partForEdit}
            onSave={onQuantitySave}
          />
        </>
      )}
    </div>
  );
};

export default List
